import { FC, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Modal } from "components/Modal";
import { ReportAlert } from "pages/UploadCSV/UploadCSVUsers/components/ReportAlert";
import { useProvisionCognitoUsers } from "./useProvisionCognitoUsers";

type ProvisionCognitoUsersModalProps = {
  open: boolean;
  onClose: () => void;
  onCompleted?: () => void;
};

export const ProvisionCognitoUsersModal: FC<ProvisionCognitoUsersModalProps> = ({
  open,
  onClose,
  onCompleted,
}) => {
  const [sendInvite, setSendInvite] = useState(true);
  const {
    loading,
    provisioning,
    completed,
    users,
    error,
    loadingData,
    report,
    provisionUsers,
    reset,
  } = useProvisionCognitoUsers(open);

  const handleClose = () => {
    if (completed) {
      onCompleted?.();
    }
    reset();
    onClose();
  };

  const progressPercent =
    loadingData.total > 0
      ? Math.floor((loadingData.current / loadingData.total) * 100)
      : 0;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title="Add Database Users to Cognito"
      size="large"
    >
      <Stack spacing={2}>
        <Typography variant="body2" color="text.secondary">
          These users exist in the database but do not have a Cognito account
          yet. They will be added one by one using the same flow as CSV import.
        </Typography>

        {!provisioning && !completed && (
          <FormControlLabel
            control={
              <Switch
                checked={sendInvite}
                onChange={(event) => setSendInvite(event.target.checked)}
              />
            }
            label="Send invite emails"
          />
        )}

        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error">{error}</Typography>
        ) : !completed && users.length === 0 ? (
          <Typography>All database users already exist in Cognito.</Typography>
        ) : !completed ? (
          <>
            <Typography variant="body2">
              {users.length} user{users.length === 1 ? "" : "s"} will be added
              to Cognito.
            </Typography>
            <TableContainer sx={{ maxHeight: 360 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Role</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.user_id}>
                      <TableCell>{user.name}</TableCell>
                      <TableCell>{user.email}</TableCell>
                      <TableCell sx={{ textTransform: "capitalize" }}>
                        {user.role}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : null}

        {provisioning && (
          <Box py={2}>
            <Typography variant="body1" align="center">
              Adding to Cognito {progressPercent}%
            </Typography>
            <LinearProgress variant="determinate" value={progressPercent} />
          </Box>
        )}

        {completed && (
          <Stack spacing={2}>
            {report.success.length > 0 && (
              <ReportAlert
                report={report.success}
                severity="success"
                textDescription="users added to Cognito successfully"
              />
            )}
            {report.error.length > 0 && (
              <ReportAlert
                report={report.error}
                severity="error"
                textDescription="users could not be added to Cognito"
              />
            )}
          </Stack>
        )}

        <Stack direction="row" justifyContent="flex-end" spacing={2}>
          {!completed ? (
            <>
              <Button
                variant="outlined"
                onClick={handleClose}
                disabled={provisioning}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                onClick={() => provisionUsers(sendInvite)}
                disabled={loading || provisioning || users.length === 0 || !!error}
              >
                {provisioning ? "Adding..." : "Confirm and Add to Cognito"}
              </Button>
            </>
          ) : (
            <Button variant="contained" onClick={handleClose}>
              Complete
            </Button>
          )}
        </Stack>
      </Stack>
    </Modal>
  );
};
